import { MultiDefnProps } from 'configuration-schemas/confdefnComponentProps';
import { isNil } from 'lodash';
import { createSelector } from 'reselect';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { AppState } from 'src/store';

//#region FlowSheetByStyle component selectors

const selectCompanionViewDataState = (state: AppState) => state.pages.assortmentBuild.flowSheet.viewDataStateCompanion;
const selectMassEditViewDataState = (state: AppState) => state.pages.assortmentBuild.flowSheet.viewDataStateMassEdit;
const selectOverTimeViewDataState = (state: AppState) => state.pages.assortmentBuild.flowSheet.viewDataStateOverTime;
const selectWorklistLiveData = (state: AppState) => state.worklist.liveData;
const selectMassEditData = (state: AppState) => state.pages.assortmentBuild.flowSheet.massEditData?.tree ?? [];

/** Determines if a viewdefn value was configured */
export const selectFlowSheetHasMassEditViewDefn = (_state: AppState, defns: MultiDefnProps) => defns.view.length >= 6;
export const selectFlowSheetHasMassEditModel = (_state: AppState, massEditModel: string | undefined) =>
  !isNil(massEditModel);

interface FlowSheetMassEditInputs {
  defns: MultiDefnProps;
  massEditModel: string | undefined;
}

/** A wrapper function to get both non redux inputs (view + model) into a selector */
export const selectFlowSheetMassEditInputs = (_state: AppState, inputs: FlowSheetMassEditInputs) => {
  const { defns, massEditModel } = inputs;
  return {
    hasMassEditModel: selectFlowSheetHasMassEditModel(_state, massEditModel),
    hasMassEditViewDefn: selectFlowSheetHasMassEditViewDefn(_state, defns),
  };
};

/**
 * Retrieve the viewDataStates to be included in the view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectFlowSheetViewDataStates = createSelector(
  selectCompanionViewDataState,
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectFlowSheetHasMassEditViewDefn,
  (viewDataStateCompanion, viewDataStateMassEdit, viewDataStateOverTime, hasMassEditViewDefn) => {
    const viewDataStates = hasMassEditViewDefn
      ? [viewDataStateCompanion, viewDataStateOverTime, viewDataStateMassEdit]
      : [viewDataStateCompanion, viewDataStateOverTime];
    return viewDataStates;
  },
);

/**
 * Retrieve just the massEdit loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectFlowSheetMassEditDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectFlowSheetMassEditInputs,
  (viewDataStateMassEdit, massEditInputs) => {
    const { hasMassEditViewDefn, hasMassEditModel } = massEditInputs;
    let dataLoaded;

    if (hasMassEditModel) {
      dataLoaded = isDataLoaded(viewDataStateMassEdit);
    } else if (hasMassEditViewDefn) {
      // when a viewdefn is configured but no model, data will be ready by the time this component is loaded
      dataLoaded = true;
    } else {
      dataLoaded = false;
    }
    return dataLoaded;
  },
);

//#endregion

//#region TabbedFlowSheetGrid component selectors

export const selectTabbedFlowSheetHasMassEditViewDefn = (_state: AppState, viewDefns: string[]) =>
  viewDefns.length >= 2;

/**
 * Retrieve massEdit data.
 *
 * For legacy reasons when no massEdit data fetch occurs, the worklist data is returned by default.
 * */
export const selectTabbedFlowSheetMassEditData = createSelector(
  selectWorklistLiveData,
  selectMassEditData,
  selectFlowSheetHasMassEditModel,
  (worklistData, massEditData, hasMassEditModel) => {
    return hasMassEditModel ? massEditData : worklistData.tree;
  },
);

/** A wrapper function to get both non redux inputs (view + model) into a selector */
export const selectTabbedFlowSheetMassEditInputs = (
  _state: AppState,
  massEditModel: string | undefined,
  viewDefns: string[],
) => {
  return {
    hasMassEditModel: selectFlowSheetHasMassEditModel(_state, massEditModel),
    hasMassEditViewDefn: selectTabbedFlowSheetHasMassEditViewDefn(_state, viewDefns),
  };
};

/**
 * Retrieve tabbed view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedFlowSheetDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectFlowSheetHasMassEditModel,
  (viewDataStateMassEdit, viewDataStateOverTime, hasMassEditModel) => {
    const massEditLoaded = isDataLoaded(viewDataStateMassEdit);
    const overTimeLoaded = isDataLoaded(viewDataStateOverTime);
    return hasMassEditModel ? massEditLoaded && overTimeLoaded : overTimeLoaded;
  },
);

/**
 * Retrieve just the massEdit loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedFlowSheetMassEditDataLoaded = createSelector(
  selectMassEditViewDataState,
  selectTabbedFlowSheetMassEditInputs,
  (viewDataStateMassEdit, massEditInputs) => {
    const { hasMassEditViewDefn, hasMassEditModel } = massEditInputs;
    let dataLoaded;

    if (hasMassEditModel) {
      dataLoaded = isDataLoaded(viewDataStateMassEdit);
    } else if (hasMassEditViewDefn) {
      // when a viewdefn is configured but no model, data will be ready by the time this component is loaded
      dataLoaded = true;
    } else {
      dataLoaded = false;
    }
    return dataLoaded;
  },
);

/**
 * Retrieve the viewDataStates to be included in the tabbed view's loading state.
 *
 * This accounts for the potential absence of massEdit data fetches.
 * */
export const selectTabbedFlowSheetViewDataStates = createSelector(
  selectMassEditViewDataState,
  selectOverTimeViewDataState,
  selectFlowSheetHasMassEditModel,
  (viewDataStateMassEdit, viewDataStateOverTime, hasMassEditModel) => {
    const viewDataStates = hasMassEditModel ? [viewDataStateOverTime, viewDataStateMassEdit] : [viewDataStateOverTime];
    return viewDataStates;
  },
);

//#endregion
